import React, { useState, useEffect, FormEvent } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../constants/appConstants';
import './LoginScreen.css';

const LoginScreen: React.FC = () => {
  const { login, loading, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [vpnConnected, setVpnConnected] = useState<boolean | null>(null);
  const [checkingVpn, setCheckingVpn] = useState(true);

  // Check VPN access on mount and every 5 seconds
  useEffect(() => {
    const checkVpn = async () => {
      try {
        const response = await axios.get(`${API_URL}/intranet/vpn-check`);
        setVpnConnected(response.data.accessible);
      } catch (err) {
        // If we can't reach our own backend, assume VPN issue
        setVpnConnected(false);
      } finally {
        setCheckingVpn(false);
      }
    };

    // Initial check
    checkVpn();

    // Poll every 5 seconds
    const interval = setInterval(checkVpn, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      return;
    }

    await login({ username: username.trim(), password });
  };

  // Show Forbidden page if VPN is not connected
  if (vpnConnected === false) {
    return (
      <div className="forbidden-page">
        <h1>Forbidden</h1>
        <p>You don't have permission to access / on this server.</p>
        <p>Additionally, a 403 Forbidden error was encountered while trying to use an ErrorDocument to handle the request.</p>
        <hr />
        <address>Apache/2.2.15 (CentOS) Server at intranet.elinkdesign.com Port 443</address>
      </div>
    );
  }

  // Show loading while checking VPN
  if (checkingVpn) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <img 
              src="https://www.elinkdesign.com/wp-content/uploads/2022/10/elink-logo-white2x.png" 
              alt="eLink Design" 
              className="login-logo-img"
            />
          </div>
          <div className="vpn-checking">
            <div className="checking-spinner"></div>
            <span>Checking VPN connection...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <img 
            src="https://www.elinkdesign.com/wp-content/uploads/2022/10/elink-logo-white2x.png" 
            alt="eLink Design" 
            className="login-logo-img"
          />
        </div>
        
        <p className="login-subtitle">
          Sign in with your intranet credentials
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-error">
              <div className="error-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="none" />
                  <path d="M12 3L2 21h20L12 3z" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="error-message">{error}</span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              type="text"
              className="form-input"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="form-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className={`login-button ${loading ? 'loading' : ''}`}
            disabled={loading || !username.trim() || !password.trim()}
          >
            {loading ? <span className="button-spinner" /> : 'Sign In'}
          </button>

          {/* <div className="vpn-notice vpn-connected">
            <svg className="vpn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10"></polyline>
            </svg>
            <span className="vpn-text">
              VPN connected
            </span>
          </div> */}
        </form>

        <div className="login-footer">
          <p className="login-footer-text">
            eLink Design - Employee Portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
